import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import useCache from '../hooks/useCache';
import TrialBanner from './TrialBanner';
import CacheIndicator from './CacheIndicator';
import {
  AppLayout,
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  Table,
  Modal,
  Form,
  FormField,
  Textarea,
  Input,
  Alert,
  Badge,
  StatusIndicator,
  Cards,
  ExpandableSection
} from '@cloudscape-design/components';

function FeatureRequests() {
  const [showAllInProgress, setShowAllInProgress] = useState(false);
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [newRequestTitle, setNewRequestTitle] = useState('');
  const [newRequestDescription, setNewRequestDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { userEmail } = useAuth();

  const fetchFeatureRequests = async () => {
    const session = await fetchAuthSession();
    const userId = session.identityId;
    const lambda = new LambdaClient({
      region: config.Region,
      credentials: session.credentials
    });

    const command = new InvokeCommand({
      FunctionName: config.FeatureRequestsFunctionArn,
      Payload: JSON.stringify({ 
        action: 'list'
      })
    });

    const response = await lambda.send(command);
    const result = JSON.parse(new TextDecoder().decode(response.Payload));
    
    if (result.statusCode === 200) {
      const data = JSON.parse(result.body);
      const requestsWithUserFlag = (data.requests || []).map(req => ({
        ...req,
        isCurrentUser: req.authorId === userId
      }));
      return { requests: requestsWithUserFlag, inProgress: data.inProgress || [], completed: data.completed || [] };
    } else {
      throw new Error('Failed to load feature requests');
    }
  };

  const { data: featureData, loading: isLoading, error: cacheError, cachedAt, isCached, refresh } = useCache('feature-requests', fetchFeatureRequests);

  const requests = featureData?.requests || [];
  const inProgressRequests = featureData?.inProgress || [];
  const completedRequests = featureData?.completed || [];

  const handleCreateRequest = async () => {
    if (!newRequestTitle.trim() || !newRequestDescription.trim()) {
      setError('Please fill in both title and description');
      return;
    }

    setIsSubmitting(true);
    try {
      const session = await fetchAuthSession();
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });

      const command = new InvokeCommand({
        FunctionName: config.FeatureRequestsFunctionArn,
        Payload: JSON.stringify({
          action: 'create',
          data: {
            title: newRequestTitle.trim(),
            description: newRequestDescription.trim()
          }
        })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));

      if (result.statusCode === 200) {
        setCreateModalVisible(false);
        setNewRequestTitle('');
        setNewRequestDescription('');
        refresh();
      } else {
        setError('Failed to create feature request');
      }
    } catch (err) {
      console.error('Error creating feature request:', err);
      setError('Failed to create feature request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVote = async (requestId) => {
    try {
      const session = await fetchAuthSession();
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });

      const command = new InvokeCommand({
        FunctionName: config.FeatureRequestsFunctionArn,
        Payload: JSON.stringify({
          action: 'vote',
          requestId: requestId
        })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));

      if (result.statusCode === 200) {
        refresh();
      }
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  const handleUnvote = async (requestId) => {
    try {
      const session = await fetchAuthSession();
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });

      const command = new InvokeCommand({
        FunctionName: config.FeatureRequestsFunctionArn,
        Payload: JSON.stringify({
          action: 'unvote',
          requestId: requestId
        })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));

      if (result.statusCode === 200) {
        refresh();
      }
    } catch (err) {
      console.error('Error unvoting:', err);
    }
  };

  const columnDefinitions = [
    {
      id: 'title',
      header: 'Feature Request',
      cell: item => (
        <Box>
          <Box fontWeight="bold">{item.title}</Box>
          <Box variant="small" color="text-body-secondary">
            {item.description}
          </Box>
        </Box>
      ),
      width: 3000,
      minWidth: 200
    },
    {
      id: 'votes',
      header: 'Votes',
      cell: item => (
        <Badge color={item.votes > 5 ? 'green' : item.votes > 2 ? 'blue' : 'grey'}>
          {item.votes}
        </Badge>
      ),
      width: 20
    },
    {
      id: 'author',
      header: 'Requested by',
      cell: item => (
        <Badge color={item.requestedBy === 'You' ? 'blue' : item.requestedBy === 'Recapify Team' ? 'green' : 'grey'}>
          {item.requestedBy}
        </Badge>
      ),
      width: 220
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: item => (
        item.userHasVoted ? (
          <Button
            variant="normal"
            size="small"
            onClick={() => handleUnvote(item.id)}
          >
            Unvote
          </Button>
        ) : (
          <Button
            variant="primary"
            size="small"
            onClick={() => handleVote(item.id)}
            disabled={item.isCurrentUser}
          >
            👍 Vote
          </Button>
        )
      ),
      width: 300
    }
  ];

  return (
    <AppLayout
      headerSelector=".sticky-nav"
      notifications={<TrialBanner />}
      navigationHide
      toolsHide
      content={
    <Container variant='embedded'>
      <SpaceBetween size="l">
        {(error || cacheError) && (
          <Alert type="error" dismissible onDismiss={() => setError('')}>
            {error || cacheError}
          </Alert>
        )}

        {/* Two-column layout for In Development and Recently Completed */}
        {(inProgressRequests.length > 0 || completedRequests.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* In Development Section */}
            {inProgressRequests.length > 0 && (
              <Container
                header={
                  <Header variant="h2">
                    In Development ({inProgressRequests.length})
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {(showAllInProgress ? inProgressRequests : inProgressRequests.slice(0, 1)).map(item => (
                    <Box key={item.id} padding={{ vertical: 's' }}>
                      <SpaceBetween size="xs">
                        <Box>
                          <StatusIndicator type="in-progress">In Development</StatusIndicator>
                        </Box>
                        <Box fontWeight="bold">{item.title}</Box>
                        <Box variant="small" color="text-body-secondary">
                          {item.description}
                        </Box>
                      </SpaceBetween>
                    </Box>
                  ))}
                  {inProgressRequests.length > 1 && (
                    <Box textAlign="center">
                      <Button
                        variant="link"
                        onClick={() => setShowAllInProgress(!showAllInProgress)}
                      >
                        {showAllInProgress ? 'Show less' : `Show ${inProgressRequests.length - 1} more`}
                      </Button>
                    </Box>
                  )}
                </SpaceBetween>
              </Container>
            )}

            {/* Recently Completed Section */}
            {completedRequests.length > 0 && (
              <Container
                header={
                  <Header variant="h2">
                    Recently Completed ({completedRequests.length})
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {(showAllCompleted ? completedRequests : completedRequests.slice(0, 1)).map(item => (
                    <Box key={item.id} padding={{ vertical: 's' }}>
                      <SpaceBetween size="xs">
                        <Box>
                          <StatusIndicator type="success">Completed</StatusIndicator>
                        </Box>
                        <Box fontWeight="bold">{item.title}</Box>
                        <Box variant="small" color="text-body-secondary">
                          {item.description}
                        </Box>
                        {item.updatedAt && (
                          <Box variant="small" color="text-body-secondary">
                            {new Date(item.updatedAt).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </Box>
                        )}
                      </SpaceBetween>
                    </Box>
                  ))}
                  {completedRequests.length > 1 && (
                    <Box textAlign="center">
                      <Button
                        variant="link"
                        onClick={() => setShowAllCompleted(!showAllCompleted)}
                      >
                        {showAllCompleted ? 'Show less' : `Show ${completedRequests.length - 1} more`}
                      </Button>
                    </Box>
                  )}
                </SpaceBetween>
              </Container>
            )}
          </div>
        )}

        {/* Feature Requests Table */}
        <Table
          columnDefinitions={columnDefinitions}
          items={requests}
          loading={isLoading}
          loadingText="Loading feature requests..."
          trackBy="id"
          wrapLines
          stripedRows
          empty={
            <Box textAlign="center" color="inherit">
              <Box variant="strong" textAlign="center" color="inherit">
                No feature requests yet
              </Box>
              <Box variant="p" padding={{ bottom: 's' }} color="inherit">
                Be the first to suggest a new feature!
              </Box>
            </Box>
          }
          header={
            <Header
              counter={`(${requests.length})`}
              description="Sorted by most votes"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <CacheIndicator cachedAt={cachedAt} isCached={isCached} loading={isLoading} onRefresh={refresh} />
                  <Button
                    variant="primary"
                    onClick={() => setCreateModalVisible(true)}
                  >
                    Create Feature Request
                  </Button>
                </SpaceBetween>
              }
            >
              Feature Requests
            </Header>
          }
        />

        {/* Create Request Modal */}
        <Modal
          visible={createModalVisible}
          onDismiss={() => {
            setCreateModalVisible(false);
            setNewRequestTitle('');
            setNewRequestDescription('');
            setError('');
          }}
          header="Create Feature Request"
          size="large"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  onClick={() => setCreateModalVisible(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateRequest}
                  loading={isSubmitting}
                >
                  Create Request
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <Form>
            <SpaceBetween size="m">
              <FormField
                label="Feature Title"
                description="Brief, descriptive title for your feature request"
                stretch
              >
                <Input
                  value={newRequestTitle}
                  onChange={({ detail }) => setNewRequestTitle(detail.value)}
                  placeholder="e.g., Dark mode support"
                />
              </FormField>
              
              <FormField
                label="Description"
                description="Detailed description of the feature and why it would be valuable"
                stretch
              >
                <Textarea
                  value={newRequestDescription}
                  onChange={({ detail }) => setNewRequestDescription(detail.value)}
                  placeholder="Describe the feature, its benefits, and how you envision it working..."
                  rows={6}
                />
              </FormField>
            </SpaceBetween>
          </Form>
        </Modal>
      </SpaceBetween>
    </Container>
      }
    />
  );
}

export default FeatureRequests;
